import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys, unwrap } from '@geefamily/api';
import { api, uploadFile } from '@/app/api';
import { useAuth } from '@/features/auth/use-auth';
import type { UserProfile } from '@/features/auth/session';

export const profileKeys = queryKeys.module('identity').entity('me');

export interface AvatarResult {
  avatarUrl: string;
  width: number;
  height: number;
  sizeBytes: number;
}

export function useProfile() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: profileKeys.detail('current'),
    queryFn: () => unwrap<UserProfile>(api.GET('/api/v1/identity/me', {})),
    enabled: isAuthenticated,
  });
}

export function useUploadAvatar() {
  const queryClient = useQueryClient();
  const { updateUser, user } = useAuth();

  return useMutation({
    mutationFn: (file: File) => uploadFile<AvatarResult>('/api/v1/identity/me/avatar', file),
    onSuccess: (result) => {
      if (user) {
        updateUser({ ...user, avatarUrl: result.avatarUrl });
      }

      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

export function useRemoveAvatar() {
  const queryClient = useQueryClient();
  const { updateUser } = useAuth();

  return useMutation({
    mutationFn: () => unwrap<UserProfile>(api.DELETE('/api/v1/identity/me/avatar', {})),
    onSuccess: (profile) => {
      updateUser(profile);
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

/**
 * Every account-settings mutation writes the profile back into the auth context.
 *
 * The context copy is what the header and the bottom bar render, so a name
 * changed here has to land there in the same tick — otherwise the page shows the
 * new name and the navigation shows the old one until something else refetches.
 */
function useProfileMutation<TInput>(mutationFn: (input: TInput) => Promise<UserProfile>) {
  const queryClient = useQueryClient();
  const { updateUser } = useAuth();

  return useMutation({
    mutationFn,
    onSuccess: (profile) => {
      updateUser(profile);
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

export function useChangeUserName() {
  return useProfileMutation((userName: string) =>
    unwrap<UserProfile>(api.PUT('/api/v1/identity/me/username', { body: { userName } })),
  );
}

export function useUpdateNotificationPreferences() {
  return useProfileMutation((body: { emailNotifications: boolean; promotionalEmails: boolean }) =>
    unwrap<UserProfile>(api.PUT('/api/v1/identity/me/preferences', { body })),
  );
}

export function useCancelEmailChange() {
  return useProfileMutation(() => unwrap<UserProfile>(api.DELETE('/api/v1/identity/me/email', {})));
}

/**
 * Starts an email change. Returns an acknowledgement, not a profile — nothing
 * has changed yet, and will not until the link in the new inbox is used.
 */
export function useRequestEmailChange() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { newEmail: string; currentPassword: string }) =>
      unwrap<{ message: string }>(api.PUT('/api/v1/identity/me/email', { body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profileKeys.all }),
  });
}

/**
 * Changes the password and ends every other session.
 *
 * Returns nothing: the profile is unaffected, and re-rendering it would only
 * suggest something about the account had moved.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      unwrap<void>(api.PUT('/api/v1/identity/me/password', { body })),
  });
}
